import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioRecorder } from './useAudioRecorder';
import { api } from '@/lib/api';

export type SpeechInputState = 'idle' | 'listening' | 'transcribing' | 'error' | 'unsupported';

interface UseWhisperInput {
  state: SpeechInputState;
  liveTranscript: string;
  elapsed: number;
  start: (onFinal: (transcript: string) => void) => Promise<void>;
  stop: () => void;
}

const AUTO_STOP_SECONDS = 60;

export function useWhisperInput(): UseWhisperInput {
  const recorder = useAudioRecorder();
  const [state, setState] = useState<SpeechInputState>(() =>
    typeof MediaRecorder === 'undefined' ? 'unsupported' : 'idle',
  );
  const onFinalRef = useRef<((transcript: string) => void) | null>(null);

  const finalize = useCallback(async () => {
    setState('transcribing');
    let blob: Blob | null = null;
    try {
      blob = await recorder.stop();
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 1500);
      return;
    }
    if (!blob || blob.size < 1024) {
      // Too short — silent return to idle.
      setState('idle');
      return;
    }
    try {
      const { transcript } = await api.voice.transcribe(blob);
      const trimmed = transcript.trim();
      if (trimmed && onFinalRef.current) {
        onFinalRef.current(trimmed);
      }
      setState('idle');
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 1500);
    } finally {
      onFinalRef.current = null;
      recorder.reset();
    }
  }, [recorder]);

  const start = useCallback(
    async (onFinal: (transcript: string) => void) => {
      if (state === 'unsupported') return;
      onFinalRef.current = onFinal;
      await recorder.start();
      if (recorder.error) {
        setState('error');
        setTimeout(() => setState('idle'), 1500);
        return;
      }
      setState('listening');
    },
    [recorder, state],
  );

  const stop = useCallback(() => {
    if (state !== 'listening') return;
    void finalize();
  }, [state, finalize]);

  useEffect(() => {
    if (state === 'listening' && recorder.durationSec >= AUTO_STOP_SECONDS) {
      void finalize();
    }
  }, [state, recorder.durationSec, finalize]);

  // If the recorder reports an error during recording, surface it.
  useEffect(() => {
    if (recorder.error && state === 'listening') {
      setState('error');
      setTimeout(() => setState('idle'), 1500);
    }
  }, [recorder.error, state]);

  return {
    state,
    liveTranscript: recorder.liveTranscript,
    elapsed: recorder.durationSec,
    start,
    stop,
  };
}
